/* eslint-disable @typescript-eslint/naming-convention */
import { useState } from 'react'
import { useToast } from '@chakra-ui/react'

import { baseStatsApiURL_NO_CACHE } from '@common/config'
import { FixtureLineup } from '@common/types'

import { useUser } from '../queries/useUser'

export interface PostLineupResponse {
    version: number
    timeline: any[]
    status: number
    statistics: any | null
    start_time: string | null
    season_id: string | null
    possible_lineups: boolean
    period_statistics: any | null
    media_contents: any | null
    lineups_editor_metadata: LineupsEditorMetadata
    id: string
    home_lineup: Lineup
    home_id: string
    feed_type: string
    events_editor_metadata: any | null
    date_modified: string
    competition_id: string | null
    category_id: string | null
    away_lineup: Lineup
    away_id: string
}

export interface LineupsEditorMetadata {
    state: string
    reason: string | null
    modified_in: string
    date_modified: string
    comment: string | null
}

export interface Lineup {
    players: Player[]
    manager_id: string | null
    formation: string | null
}

export interface Player {
    starter: boolean
    position: string | null
    player_id: string
    played: boolean
    order: number | null
    name: string
    jersey_number: number
}

export function useCreateNewLineup() {
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const { userData } = useUser()

    const toast = useToast()

    const postData = async (data: FixtureLineup): Promise<PostLineupResponse | undefined> => {
        setIsError(false)

        setIsLoading(true)

        let responseData: PostLineupResponse | undefined

        try {
            if (!userData?.accessToken) {
                throw new Error('MISSING_TOKEN')
            }

            const response = await fetch(`${baseStatsApiURL_NO_CACHE}/internal/editor/lineups/lineup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authentication: userData.accessToken,
                },
                body: JSON.stringify(data),
            })

            if (!response.ok) {
                throw new Error('REQUEST_FAILED')
            }

            responseData = await response.json()

            toast({
                position: 'top',
                title: 'New Lineup successfully created',
                description: 'It will be updated in few seconds',
                status: 'success',
            })
        } catch (error) {
            setIsError(true)

            if (error instanceof Error && error.message === 'MISSING_TOKEN') {
                toast({
                    position: 'top',
                    description: 'Missing user access token!',
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                })
            } else {
                toast({ position: 'top', title: 'Lineup creation failed, please try again!', status: 'error' })
            }
        }

        setIsLoading(false)

        return responseData
    }

    return { postData, isLoading, isError }
}
